import { styled } from '@linaria/react';

import { bodyFont, colors, fonts, layout, semantic, space } from '../../theme/tokens';

export const TopBarSlot = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: ${space[6]} ${space[5]} 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[7]} ${space[7]} 0;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[6]};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: ${space[9]} ${space[5]} ${space[7]};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[10]} ${space[11]} ${space[9]};
  }
`;

export const TopBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
`;

export const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 ${space['20px']};
  border: none;
  border-radius: ${layout.borderRadiusFull};
  background: ${semantic.bg.surfaceSubtle};
  color: ${colors.black};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

export const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[1]};
  width: 100px;
`;

export const ProgressSegment = styled.div<{ $active?: boolean }>`
  flex: 1 0 0;
  height: 4px;
  border-radius: ${layout.borderRadiusFull};
  background: ${({ $active }) =>
    $active ? semantic.bg.positiveStrong : semantic.bg.surfaceSubtle};
`;

export const ProgressDot = styled.div<{ $active?: boolean }>`
  width: 4px;
  height: 4px;
  border-radius: ${layout.borderRadiusFull};
  background: ${({ $active }) =>
    $active ? semantic.bg.positiveStrong : semantic.bg.surfaceSubtle};
`;

export const RestaurantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
`;

export const RestaurantOption = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${space['20px']};
  width: 100%;
  min-height: 72px;
  padding: ${space['20px']};
  border: ${({ $selected }) =>
    $selected ? `2px solid ${semantic.border.selected}` : `1px solid ${semantic.border.default}`};
  border-radius: ${layout.borderRadiusXl};
  background: ${semantic.bg.canvas};
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

export const RestaurantOptionText = styled.span`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: ${space[1]};
  min-width: 0;
`;

export const RestaurantName = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: 1.4;
  color: ${semantic.text.primary};
`;

export const RestaurantSubtitle = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: 1.4;
  color: ${semantic.text.secondary};
`;

export const RestaurantThumb = styled.img`
  width: 60px;
  height: 60px;
  border-radius: ${layout.borderRadius};
  object-fit: cover;
  flex-shrink: 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    width: 80px;
    height: 80px;
  }
`;

export const RestaurantThumbPlaceholder = styled.span`
  width: 60px;
  height: 60px;
  border-radius: ${layout.borderRadius};
  background: ${semantic.bg.surfaceSubtle};
  flex-shrink: 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    width: 80px;
    height: 80px;
  }
`;

export const CardSection = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${space[5]};
  padding: ${({ $compact }) =>
    $compact ? `${space[5]} ${space['20px']}` : `${space[7]} ${space['20px']}`};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${({ $compact }) =>
      $compact ? `${space['36px']} ${space[6]} ${space[8]}` : `${space[8]} ${space[6]}`};
  }
`;

export const FieldLabel = styled.p`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: 1.4;
  color: ${semantic.text.primary};
`;

export const TimeslotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${space[2]};
  width: 100%;

  @media (min-width: ${layout.desktopBreakpoint}) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

export const EmptyTimeslots = styled.p`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: 1.4;
  color: ${semantic.text.secondary};
`;

export const MenuCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  width: 100%;
  box-sizing: border-box;
  padding: ${space[5]} ${space['20px']};
  border-radius: ${layout.borderRadiusXl};
  background: ${semantic.bg.surfaceSubtle};
`;

export const MenuLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[2]};
`;

export const MenuLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: ${space[3]} ${space['20px']};
  border: 1px solid ${semantic.border.default};
  border-radius: ${layout.borderRadiusFull};
  background: ${semantic.bg.canvas};
  color: ${colors.black};
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.medium};
  line-height: 1.4;
  text-decoration: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

export const DepositBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[3]} ${space[4]};
  border: 1px solid ${semantic.border.info};
  border-radius: ${layout.borderRadiusMd};
  background: ${semantic.bg.infoSubtle};
  color: ${semantic.text.info};
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: 1.4;
`;

export const BottomBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[4]};
  width: 100%;
  box-sizing: border-box;
  padding: ${space[3]} ${space['20px']};
  background: ${semantic.bg.canvas};
  border-top: 1px solid ${semantic.border.default};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[5]} ${space[7]};
  }
`;

export const BottomBarTitle = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: 1.4;
  color: ${semantic.text.primary};
`;
